// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

export interface AffiliatePromoAsset {
  id: string;
  name: string;
  file_path: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  display_type: string;
  assigned_to_all: boolean;
  assigned_affiliate_ids: string[];
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

export interface AffiliateProfile {
  id: string;
  name: string;
  email: string;
}

export const useAffiliatePromoAssets = () => {
  const [assets, setAssets] = useState<AffiliatePromoAsset[]>([]);
  const [affiliates, setAffiliates] = useState<AffiliateProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchAssets = async () => {
    try {
      const { data, error } = await supabase
        .from('affiliate_promo_assets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching promo assets:', error);
        toast({
          title: "Error",
          description: "Failed to load promo assets",
          variant: "destructive",
        });
        return;
      }

      setAssets(data || []);
    } catch (error) {
      console.error('Error in fetchAssets:', error);
    }
  };

  const fetchAffiliates = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, email')
        .eq('role', 'affiliate')
        .order('name');

      if (error) {
        console.error('Error fetching affiliates:', error);
        return;
      }

      setAffiliates(data || []);
    } catch (error) {
      console.error('Error in fetchAffiliates:', error);
    }
  };

  const uploadAsset = async (
    file: File,
    name: string,
    assignedToAll: boolean,
    assignedAffiliateIds: string[],
    displayType: string
  ): Promise<boolean> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('affiliate-promo-assets')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        toast({
          title: "Upload Error",
          description: uploadError.message,
          variant: "destructive",
        });
        return false;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('affiliate-promo-assets')
        .getPublicUrl(filePath);

      // Insert metadata into database
      const { error: dbError } = await supabase
        .from('affiliate_promo_assets')
        .insert({
          name,
          file_path: filePath,
          file_url: publicUrl,
          file_size: file.size,
          mime_type: file.type,
          display_type: displayType,
          assigned_to_all: assignedToAll,
          assigned_affiliate_ids: assignedToAll ? [] : assignedAffiliateIds
        });

      if (dbError) {
        console.error('Database insert error:', dbError);
        toast({
          title: "Database Error",
          description: dbError.message,
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Promo asset uploaded successfully",
      });

      await fetchAssets();
      return true;
    } catch (error) {
      console.error('Error uploading asset:', error);
      toast({
        title: "Error",
        description: "Failed to upload promo asset",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteAsset = async (assetId: string, filePath: string): Promise<boolean> => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('affiliate-promo-assets')
        .remove([filePath]);

      if (storageError) {
        console.error('Storage delete error:', storageError);
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('affiliate_promo_assets')
        .delete()
        .eq('id', assetId);

      if (dbError) {
        console.error('Database delete error:', dbError);
        toast({
          title: "Error",
          description: dbError.message,
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Promo asset deleted successfully",
      });

      await fetchAssets();
      return true;
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast({
        title: "Error",
        description: "Failed to delete promo asset",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAssets(), fetchAffiliates()]);
      setLoading(false);
    };

    loadData();
  }, []);

  return {
    assets,
    affiliates,
    loading,
    uploadAsset,
    deleteAsset,
    refetch: fetchAssets
  };
};
